import React from 'react';
import { motion } from 'framer-motion';
import { Film, Camera, Palette, Play, Settings } from 'lucide-react';

const SyntaraCanvas: React.FC = () => {
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="p-3 border-b border-slate-700/50 flex-shrink-0">
        <h3 className="text-lg font-medium violett-gradient-text text-drop-shadow">Syntara</h3>
        <p className="text-sm text-slate-400 mt-1">Cinematic Engine & Visual Studio</p>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Film className="w-6 h-6 text-purple-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Active Projects</h4>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Product Demo Reel</span>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Rendering</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">High-quality product showcase animation</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className="bg-gradient-to-r from-purple-400 to-pink-500 h-2 rounded-full" style={{ width: '73%' }}></div>
                  </div>
                  <span className="text-xs text-slate-400">73%</span>
                </div>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">UI Transitions</span>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">In Progress</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Smooth interface animation sequences</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className="bg-gradient-to-r from-blue-400 to-cyan-500 h-2 rounded-full" style={{ width: '45%' }}></div>
                  </div>
                  <span className="text-xs text-slate-400">45%</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Camera className="w-6 h-6 text-cyan-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Render Queue</h4>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="font-medium">Scene_001.mp4</span>
                </div>
                <span className="text-sm font-medium text-green-400">Rendering</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span className="font-medium">Transition_A.mp4</span>
                </div>
                <span className="text-sm font-medium text-yellow-400">Queued</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="font-medium">Logo_Anim.mp4</span>
                </div>
                <span className="text-sm font-medium text-blue-400">Queued</span>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Palette className="w-6 h-6 text-pink-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Asset Library</h4>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">3D Models</span>
                  <span className="text-xs text-slate-400">247 assets</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">High-quality 3D models and meshes</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Textures</span>
                  <span className="text-xs text-slate-400">1,432 files</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">PBR materials and texture maps</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Animations</span>
                  <span className="text-xs text-slate-400">89 sequences</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Pre-built animation sequences</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Settings className="w-6 h-6 text-orange-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Engine Status</h4>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">GPU Utilization</span>
                  <span className="text-sm font-medium text-green-400">87%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full" style={{ width: '87%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Memory Usage</span>
                  <span className="text-sm font-medium text-blue-400">12.4 GB</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full" style={{ width: '62%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Render Speed</span>
                  <span className="text-sm font-medium text-cyan-400">24 fps</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-cyan-400 to-blue-500 h-2 rounded-full" style={{ width: '80%' }}></div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        <motion.div 
          className="mt-6 glass-dark rounded-xl p-6 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <Play className="w-6 h-6 text-green-400" />
            <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Recent Activity</h4>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-sm">Completed render: Product_Demo_Final.mp4 (4K, 60fps)</span>
              <span className="text-xs text-slate-400 ml-auto">5 min ago</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span className="text-sm">Imported new asset pack: Sci-Fi Environment Kit</span>
              <span className="text-xs text-slate-400 ml-auto">18 min ago</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              <span className="text-sm">Created new scene: UI_Showcase_v2.scene</span>
              <span className="text-xs text-slate-400 ml-auto">32 min ago</span>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default SyntaraCanvas;
